import { Injectable } from '@angular/core';
import { ImageChatCodeGenerationService } from '@examdojo/chat';
import { concatMap, EMPTY, Observable, of, Subject, tap, catchError } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { mapToVoid } from '@examdojo/core/rxjs';
import { QuestionImageGenerationService } from '../services/question-image-generation.service';
@Injectable()
export class QuestionImageChatCodeGenerationService extends ImageChatCodeGenerationService {
  constructor(private readonly questionImageGenerationService: QuestionImageGenerationService) {
    super();
    this.observeCodeGenerationQueue().pipe(takeUntilDestroyed()).subscribe();
  }

  private readonly chatImageAndCodeApplied$$ = new Subject<{ image: File | null; code: string }>();
  readonly chatImageAndCodeApplied$ = this.chatImageAndCodeApplied$$.asObservable();

  // api returns broken images if run couple of images in parallel
  private readonly generateCodeQueue$ = new Subject<{ image: string; resultSubject: Subject<File | null> }>();

  generateCode(image: string): Observable<File | null> {
    const resultSubject = new Subject<File | null>();
    this.generateCodeQueue$.next({ image, resultSubject });
    return resultSubject.asObservable();
  }

  applyCodeAndImage(image: File | null, code: string): Observable<void> {
    const imageObservable$ = image ? of(image) : this.questionImageGenerationService.generateImage(code);

    return imageObservable$.pipe(
      tap((generatedImage) => {
        this.chatImageAndCodeApplied$$.next({ image: generatedImage, code });
      }),
      mapToVoid(),
    );
  }

  private observeCodeGenerationQueue(): Observable<File | null> {
    return this.generateCodeQueue$.pipe(
      concatMap(({ image, resultSubject }) =>
        this.questionImageGenerationService.generateImage(image).pipe(
          tap((result) => resultSubject.next(result)),
          tap(() => resultSubject.complete()),
          catchError((error: unknown) => {
            resultSubject.error(error);
            return EMPTY;
          }),
        ),
      ),
    );
  }
}
